import occtimportjs from 'occt-import-js';
import * as THREE from 'three';
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { ColladaLoader } from 'three/examples/jsm/loaders/ColladaLoader';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { Rhino3dmLoader } from 'three/examples/jsm/loaders/3DMLoader';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js'; // import { OBJLoader2 } from 'wwobjloader2';

import { buildThreeGroupFromUsdStage } from '@/services/usdStageToThree';
import { v4 as uuidv4 } from 'uuid';
import { validate as uuidValidate } from 'uuid';

export type LoadSource = string | File;
export type LoadPostProcessingOptions = {
  filename?: string;
  castShadows?: boolean;
  scale?: number;
  setUUID?: boolean;
  removeWrapperbasedOnUUID?: boolean;
};
export class LoadInformation {
  uri: string;
  filename: string;
  fileExtension: string;

  constructor(source: LoadSource) {
    if (source instanceof File) {
      this.filename = source.name;
      this.uri = URL.createObjectURL(source);
    } else {
      this.filename = source.substring(source.lastIndexOf('/') + 1);
      this.uri = source;
    }
    this.fileExtension = LoadInformation.getFileExtension(this.filename);
  }

  isSameAs(source: LoadSource): boolean {
    if (source instanceof File) {
      return this.filename === source.name;
    } else {
      return this.uri === source;
    }
  }
  static getFileExtension(nameOrUri: string): string {
    const fileExtensionIndex = nameOrUri.lastIndexOf('.');
    return fileExtensionIndex !== -1
      ? nameOrUri.substring(fileExtensionIndex, nameOrUri.length).toLocaleLowerCase()
      : '';
  }
}

export class LoadManager {
  /** Lazily initialized ThatOpen stack (Components + Fragments worker + IfcLoader). */
  private static ifcComponentsPromise: Promise<import('@thatopen/components').Components> | null =
    null;

  private static ifcModulePromise: Promise<typeof import('@thatopen/components')> | null = null;
  /** web-ifc/Fragments share singleton state and must not process multiple models concurrently. */
  private static ifcLoadQueue: Promise<void> = Promise.resolve();

  private static getIfcModule(): Promise<typeof import('@thatopen/components')> {
    if (!LoadManager.ifcModulePromise) {
      LoadManager.ifcModulePromise = import('@thatopen/components');
    }
    return LoadManager.ifcModulePromise;
  }

  private static occtLoader: any = undefined;
  private static gltfLoader = new GLTFLoader(); //gltfLoader.setWasmPath('')
  private static colladaLoader = new ColladaLoader();
  private static stlLoader = new STLLoader();
  private static plyLoader = new PLYLoader();
  private static fbxLoader = new FBXLoader();
  private static objLoader = new OBJLoader();
  private static rhino3dmLoader = new Rhino3dmLoader(); // rhino3dmLoader.setLibraryPath( 'https://cdn.jsdelivr.net/npm/rhino3dm@8.0.1' );

  public static readonly defaultMaterial = new THREE.MeshPhongMaterial({
    color: 0xaaaaaa, //color: 0xff5533,
    specular: 0xffffff, //specular: 0x111111,
    shininess: 250,
    side: THREE.DoubleSide,
  });

  public static readonly defaultVertexMaterial = new THREE.MeshPhongMaterial({
    shininess: 250,
    side: THREE.DoubleSide,
    vertexColors: true,
  });

  /**
   * Loads model with suited importer and does specified post processing
   * @param loadInfo
   * @param func
   * @param options
   */
  public static loadModel(
    { uri, filename, fileExtension }: LoadInformation,
    func: (occtModel: THREE.Object3D) => void,
    options: LoadPostProcessingOptions = {
      filename: filename,
      castShadows: false,
      removeWrapperbasedOnUUID: false,
    },
    onError?: (error: unknown) => void
  ): void {
    options.filename = filename;
    const loadFunction = LoadManager.postProcessing(func, options);
    const scaleCorrection = ['.stl', '.fbx', '.step', '.stp', '.igs', '.iges', '.brep'];
    if (scaleCorrection.includes(fileExtension)) options['scale'] = 0.001;

    switch (fileExtension) {
      case '.stl':
        LoadManager.stlLoader.load(
          uri,
          (model: THREE.BufferGeometry) => {
            loadFunction(new THREE.Mesh(model, LoadManager.defaultMaterial));
          },
          undefined,
          (error) => onError?.(error)
        );
        break;
      case '.ply':
        LoadManager.plyLoader.load(
          uri,
          (model: THREE.BufferGeometry) => {
            loadFunction(new THREE.Mesh(model, LoadManager.defaultVertexMaterial));
          },
          undefined,
          (error) => onError?.(error)
        );
        break;
      case '.dae':
        LoadManager.colladaLoader.load(
          uri,
          (collada) => {
            if (collada) loadFunction(collada.scene);
            else onError?.(new Error('COLLADA_LOAD_FAILED'));
          },
          undefined,
          (error) => onError?.(error)
        );
        break;
      case '.glb':
      case '.gltf':
        LoadManager.gltfLoader.load(
          uri,
          (gltf: GLTF) => loadFunction(gltf.scene),
          undefined,
          (error) => onError?.(error)
        );
        break;
      case '.ifc':
        void LoadManager.loadIfcWithThatOpen(uri, filename, loadFunction).catch((error) => {
          console.error('IFC load failed:', error);
          onError?.(error);
        });
        break;
      case '.fbx':
        LoadManager.fbxLoader.load(uri, loadFunction, undefined, (error) => onError?.(error));
        break;
      case '.obj':
        LoadManager.objLoader.load(uri, loadFunction, undefined, (error) => onError?.(error));
        break;
      case '.3dm':
        LoadManager.rhino3dmLoader.load(uri, loadFunction, undefined, (error) => onError?.(error));
        break;
      case '.stp':
      case '.step':
      case '.igs':
      case '.iges':
      case '.brep':
        void LoadManager.loadOcct(uri, fileExtension, loadFunction).catch((error) => {
          console.error('OCCT load failed:', error);
          onError?.(error);
        });
        break;
      case '.usd':
      case '.usda':
      case '.usdc':
      case '.usdz':
        void LoadManager.loadUsdAsset(uri, filename, loadFunction).catch((error) => {
          console.error('USD load failed:', error);
          onError?.(error);
        });
        break;
      default:
        onError?.(new Error(`Unsupported model type: ${fileExtension || '(none)'}`));
        break;
    }
  }

  /** ZIP local file header (USDZ packages are zip archives). */
  private static isZipFile(buffer: ArrayBuffer): boolean {
    if (buffer.byteLength < 4) return false;
    const v = new Uint8Array(buffer, 0, 4);
    return v[0] === 0x50 && v[1] === 0x4b;
  }

  /**
   * USD: three.js USDLoader/USDA path uses JSON.parse on string fragments and breaks on real-world USDA.
   * We parse with @cinevva/usdjs, compose layers, and build meshes from SdfLayer data (see usdStageToThree.ts).
   */
  private static async loadUsdAsset(
    uri: string,
    filename: string,
    loadFunction: (model: THREE.Object3D) => void
  ): Promise<void> {
    try {
      const { UsdStage } = await import('@/generated/usdjs-bridge.js');
      const buffer = await LoadManager.fetchToArrayBuffer(uri);
      const lower = filename.toLowerCase();
      const ab = buffer as ArrayBuffer;

      const treatAsUsdz =
        lower.endsWith('.usdz') || (lower.endsWith('.usd') && LoadManager.isZipFile(ab));

      let stage: import('@/generated/usdjs-bridge.js').UsdStage;
      if (treatAsUsdz) {
        stage = await UsdStage.openUSDZ(new Uint8Array(ab), filename);
      } else if (lower.endsWith('.usda')) {
        stage = UsdStage.open(new TextDecoder('utf-8').decode(new Uint8Array(ab)), filename);
      } else {
        stage = UsdStage.open(ab, filename);
      }

      const group = buildThreeGroupFromUsdStage(stage);
      loadFunction(group);
    } catch (error) {
      console.error('USD load failed:', error);
      throw error;
    }
  }

  private static postProcessing(
    func: (model: THREE.Object3D) => void,
    options: LoadPostProcessingOptions
  ): (model: THREE.Object3D) => void {
    function process(model: THREE.Object3D) {
      if (
        options.removeWrapperbasedOnUUID === true &&
        !model.userData['uuidv4'] &&
        model.children[0]?.userData['uuidv4']
      ) {
        model = model.children[0];
      }
      if (options.filename) {
        model.name = options.filename;
        model.userData['name'] = options.filename;
      }
      if (options.castShadows === true) LoadManager.castShadows(model);
      if (options.scale) model.scale.set(options.scale, options.scale, options.scale);
      if (options.setUUID && !model.userData['uuidv4']) model.userData['uuidv4'] = uuidv4();
      func(model);
    }
    return process;
  }

  /**
   * Same-origin URL for the Fragments worker (public/fragments-worker after postinstall).
   * Avoids blob: URLs from unpkg, which break source map resolution (Safari / WebKit noise and errors).
   * `.js`, not `.mjs`: nginx serves .mjs as application/octet-stream and module workers then fail to load.
   */
  private static getFragmentsWorkerUrl(): string {
    const baseUrl =
      typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL != null
        ? import.meta.env.BASE_URL
        : '/';
    const normalized = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    if (typeof window === 'undefined') {
      return `${normalized}fragments-worker/worker.js`;
    }
    return `${window.location.origin}${normalized}fragments-worker/worker.js`;
  }

  /**
   * Base URL for web-ifc WASM files (served from public/web-ifc after postinstall copy).
   */
  private static getWebIfcWasmBaseUrl(): string {
    const baseUrl =
      typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL != null
        ? import.meta.env.BASE_URL
        : '/';
    const normalized = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    if (typeof window === 'undefined') {
      return `${normalized}web-ifc/`;
    }
    return `${window.location.origin}${normalized}web-ifc/`;
  }

  /**
   * Ensures a single ThatOpen Components instance with FragmentsManager and IfcLoader.
   * WASM is loaded from /web-ifc/* (not unpkg) so Vite serves correct files and web-ifc is not pre-bundled.
   */
  private static getIfcComponents(): Promise<import('@thatopen/components').Components> {
    if (!LoadManager.ifcComponentsPromise) {
      LoadManager.ifcComponentsPromise = (async () => {
        const OBC = await LoadManager.getIfcModule();
        const components = new OBC.Components();
        components.init();
        const fragments = components.get(OBC.FragmentsManager);
        fragments.init(LoadManager.getFragmentsWorkerUrl());
        const ifcLoader = components.get(OBC.IfcLoader);
        await ifcLoader.setup({
          autoSetWasm: false,
          wasm: {
            path: LoadManager.getWebIfcWasmBaseUrl(),
            absolute: true,
          },
        });
        return components;
      })();
    }
    return LoadManager.ifcComponentsPromise;
  }

  /** Normalize Fragments material colors (THREE.Color or plain {r,g,b} / 0–255). */
  private static colorFromFragmentsMaterial(raw: unknown): THREE.Color {
    if (raw instanceof THREE.Color) return raw.clone();
    if (raw && typeof raw === 'object') {
      const c = raw as { r?: number; g?: number; b?: number; isColor?: boolean };
      if (typeof c.r === 'number' && typeof c.g === 'number' && typeof c.b === 'number') {
        // Fragments sometimes returns 0–255 channel values instead of 0–1.
        const scale = c.r > 1 || c.g > 1 || c.b > 1 ? 1 / 255 : 1;
        return new THREE.Color(c.r * scale, c.g * scale, c.b * scale);
      }
    }
    if (Array.isArray(raw) && raw.length >= 3) {
      const [r, g, b] = raw as number[];
      const scale = r > 1 || g > 1 || b > 1 ? 1 / 255 : 1;
      return new THREE.Color(r * scale, g * scale, b * scale);
    }
    return new THREE.Color(0xaeaeae);
  }

  /**
   * Bake FragmentsModel geometry into a plain THREE.Group for GLB export.
   * Fragments stream tiles via camera LOD — without this, model.object is often empty.
   */
  private static async bakeFragmentsModelToObject3D(
    model: import('@thatopen/fragments').FragmentsModel
  ): Promise<THREE.Object3D> {
    const localIds = await model.getItemsIdsWithGeometry();
    if (!localIds.length) {
      throw new Error('IFC_NO_GEOMETRY');
    }

    const colorByLocalId = new Map<number, THREE.Color>();
    const opacityByLocalId = new Map<number, number>();
    const materialCache = new Map<string, THREE.MeshStandardMaterial>();
    const materialDefs = await model.getItemsMaterialDefinition(localIds);
    for (const entry of materialDefs) {
      for (const id of entry.localIds) {
        colorByLocalId.set(id, LoadManager.colorFromFragmentsMaterial(entry.definition?.color));
        opacityByLocalId.set(id, entry.definition?.opacity ?? 1);
      }
    }

    const group = new THREE.Group();
    const CHUNK = 400;
    for (let offset = 0; offset < localIds.length; offset += CHUNK) {
      const chunk = localIds.slice(offset, offset + CHUNK);
      const meshDataByItem = await model.getItemsGeometry(chunk, 0);
      for (let i = 0; i < chunk.length; i += 1) {
        const localId = chunk[i];
        const itemMeshes = meshDataByItem[i];
        if (!itemMeshes?.length) continue;

        for (const meshData of itemMeshes) {
          const { indices, positions, normals, transform } = meshData;
          if (!indices || !positions || indices.length < 3 || positions.length < 9) continue;

          const geometry = new THREE.BufferGeometry();
          geometry.setIndex(new THREE.BufferAttribute(indices, 1));
          const pos =
            positions instanceof Float32Array
              ? positions
              : Float32Array.from(positions as ArrayLike<number>);
          geometry.setAttribute('position', new THREE.BufferAttribute(pos, 3));

          if (normals && normals.length === pos.length) {
            if (normals instanceof Int16Array) {
              const decoded = new Float32Array(normals.length);
              for (let n = 0; n < normals.length; n += 1) {
                decoded[n] = normals[n] / 32767;
              }
              geometry.setAttribute('normal', new THREE.BufferAttribute(decoded, 3));
            } else {
              geometry.setAttribute(
                'normal',
                new THREE.BufferAttribute(normals as Float32Array, 3, true)
              );
            }
          } else {
            geometry.computeVertexNormals();
          }

          const opacity = opacityByLocalId.get(localId) ?? 1;
          const color = colorByLocalId.get(localId) ?? new THREE.Color(0xaeaeae);
          const materialKey = `${color.getHexString()}:${opacity.toFixed(4)}`;
          let material = materialCache.get(materialKey);
          if (!material) {
            material = new THREE.MeshStandardMaterial({
              color: color.clone(),
              metalness: 0.15,
              roughness: 0.8,
              side: THREE.DoubleSide,
              transparent: opacity < 0.999,
              opacity,
            });
            materialCache.set(materialKey, material);
          }
          const mesh = new THREE.Mesh(geometry, material);
          if (transform) mesh.applyMatrix4(transform);
          group.add(mesh);
        }
      }
    }

    if (group.children.length === 0) {
      throw new Error('IFC_NO_GEOMETRY');
    }

    // Match Fragments scene transform when present.
    if (model.object) {
      model.object.updateMatrixWorld(true);
      group.applyMatrix4(model.object.matrixWorld);
    }
    return group;
  }

  /**
   * Loads IFC via @thatopen/components and bakes Fragments into a static THREE tree for GLB export.
   */
  private static async loadIfcWithThatOpen(
    uri: string,
    filename: string,
    loadFunction: (model: THREE.Object3D) => void
  ): Promise<void> {
    const run = () => LoadManager.loadIfcWithThatOpenSerial(uri, filename, loadFunction);
    const pending = LoadManager.ifcLoadQueue.then(run, run);
    LoadManager.ifcLoadQueue = pending.catch(() => undefined);
    return pending;
  }

  private static async loadIfcWithThatOpenSerial(
    uri: string,
    filename: string,
    loadFunction: (model: THREE.Object3D) => void
  ): Promise<void> {
    const OBC = await LoadManager.getIfcModule();
    const components = await LoadManager.getIfcComponents();
    const ifcLoader = components.get(OBC.IfcLoader);
    let model: import('@thatopen/fragments').FragmentsModel | null = null;
    try {
      const buffer = await LoadManager.fetchToArrayBuffer(uri);
      const data = new Uint8Array(buffer);
      const baseName = filename.replace(/\.ifc$/i, '') || 'ifc-model';
      const modelId = `${baseName}-${uuidv4()}`;
      model = await ifcLoader.load(data, true, modelId);

      // Prefer an explicit bake — Fragments tiles need a camera to stream into model.object.
      const baked = await LoadManager.bakeFragmentsModelToObject3D(model);
      loadFunction(baked);
    } finally {
      if (model) {
        try {
          await model.dispose();
        } catch (disposeError) {
          console.warn('IFC fragments dispose warning:', disposeError);
        }
      }
      if (uri.startsWith('blob:')) URL.revokeObjectURL(uri);
    }
  }

  public static createMeshFromGeometry(
    geometry: THREE.BufferGeometry,
    material: THREE.Material = LoadManager.defaultMaterial
  ): THREE.Mesh {
    return new THREE.Mesh(geometry, material);
  }

  /**
   * Recursively set castShadow to true for all meshes in the model
   * @param model
   */
  public static castShadows(model: THREE.Object3D) {
    model.traverse((child) => {
      if (child.type === 'Mesh') child.castShadow = true;
    });
  }

  /**
   * Fetches a file and returns it as an array buffer
   * @param fileOrUri
   * @returns
   */
  public static async fetchToArrayBuffer(fileOrUri: string | File) {
    const uri = fileOrUri instanceof File ? URL.createObjectURL(fileOrUri) : fileOrUri;
    const response = await fetch(uri);
    const arrayBuffer = await response.arrayBuffer();
    if (fileOrUri instanceof File) URL.revokeObjectURL(uri);
    return arrayBuffer;
  }

  private static async loadOcct(
    uri: string,
    extension: string,
    func: (step: THREE.Object3D) => void
  ): Promise<void> {
    if (LoadManager.occtLoader === undefined) LoadManager.occtLoader = await occtimportjs();
    let fileBuffer = new Uint8Array(await LoadManager.fetchToArrayBuffer(uri));
    let result;
    switch (extension) {
      case '.brep':
        result = LoadManager.occtLoader.ReadBrepFile(fileBuffer, null);
        break;
      case '.iges':
      case '.igs':
        result = LoadManager.occtLoader.ReadIgesFile(fileBuffer, null);
        break;
      case '.stp':
      case '.step':
        result = LoadManager.occtLoader.ReadStepFile(fileBuffer, null);
        break;
      default:
        throw new Error(`Unsupported OCCT extension: ${extension}`);
    }
    if (!result || !result.meshes?.length) {
      throw new Error('OCCT_LOAD_FAILED');
    }
    func(LoadManager.createGroupFromOcctResult(result));
  }

  private static createGroupFromOcctResult(result: any): THREE.Group {
    const group = new THREE.Group();
    for (let resultMesh of result.meshes) {
      let geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(resultMesh.attributes.position.array, 3)
      );
      if (resultMesh.attributes.normal) {
        geometry.setAttribute(
          'normal',
          new THREE.Float32BufferAttribute(resultMesh.attributes.normal.array, 3)
        );
      }
      const index = Uint32Array.from(resultMesh.index.array);
      geometry.setIndex(new THREE.BufferAttribute(index, 1));

      let material = null;
      if (resultMesh.color) {
        const color = new THREE.Color(
          resultMesh.color[0],
          resultMesh.color[1],
          resultMesh.color[2]
        );
        material = new THREE.MeshPhongMaterial({
          color: color,
        });
      } else {
        material = new THREE.MeshPhongMaterial({
          color: 0xcccccc,
        });
      }
      const mesh = new THREE.Mesh(geometry, material);
      group.add(mesh);
    }
    group.rotation.set(THREE.MathUtils.degToRad(-90), 0, 0);
    return group;
  }
}

/**
 * A function that extracts and validates a UUID from the given name string.
 *
 * @param {string} name - The input name string that may contain a UUID.
 * @return {string} The extracted and validated UUID if found, otherwise an empty string.
 */
export function getValidatedUUID(name: string) {
  const idIndex = name.lastIndexOf('_');
  if (idIndex === -1) return '';
  const id = name.substring(idIndex + 1);
  return uuidValidate(id) ? id : '';
}

/**
 * Removes the UUID file extension from the given name.
 *
 * @param {string} name - The name from which to remove the UUID file extension.
 * @return {string} The name with the UUID file extension removed.
 */
export function removeUUIDFileExtension(name: string) {
  let result = name;
  const fileExtension = LoadInformation.getFileExtension(name);
  if (fileExtension) result = result.replace(fileExtension, '');
  const id = getValidatedUUID(result);
  if (id) result = result.replace('_' + id, '');
  return result;
}
